import { useState, useEffect } from "react";
import { AGRO_API_KEY, convertTempKelvinToCelsius } from "../../utils/Helpers";
import { NDVIData, SoilData, WeatherData } from "../../types/agroTypes";
import { AgroMCardProps } from "../../types/propsTypes";

const WeatherDisplay = ({ data }: { data: WeatherData; }) => {
    return (
        <div className="shadow-md rounded-lg p-4 mt-2 w-full mx-auto">
            <h2 className="text-sm font-semibold mb-2 text-slate-500 uppercase">Current Weather</h2>
            <div className="border-t border-slate-500 w-full mt-2">
                {Object.entries({
                    "Date": `${new Date(data.dt * 1000).toLocaleString()}`,
                    "Temperature": `${convertTempKelvinToCelsius(data.main.temp)} °C`,
                    "Pressure": `${data.main.pressure} hPa`,
                    "Humidity": `${data.main.humidity} %`,
                    "Wind Speed": `${data.wind.speed} m/s`,
                    "Wind Direction": `${data.wind.deg}°`,
                    "Weather Info": data.weather[0].main
                }).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-gray-300 py-2 text-sm">
                        <span className="font-medium text-gray-600">{key}</span>
                        <span className="text-slate-500">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SoilDisplay = ({ data }: { data: SoilData; }) => {
    return (
        <div className="shadow-md rounded-lg p-4 mt-2 w-full mx-auto">
            <h2 className="text-sm font-semibold mb-2 text-slate-500 uppercase">Current Soil Info</h2>
            <div className="border-t border-slate-500 w-full mt-2">
                {Object.entries({
                    "Date": `${new Date(data.dt * 1000).toLocaleString()}`,
                    "Temperature((10cm depth)": `${convertTempKelvinToCelsius(data.t10)} °C`,
                    "Moisture": `${data.moisture}`,
                    "Temperature(Surface)": `${convertTempKelvinToCelsius(data.t0)} °C`,
                }).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-slate-500 py-2 text-sm">
                        <span className="font-medium text-gray-600">{key}</span>
                        <span className="text-slate-500">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const NDVIDisplay = ({ data }: { data: NDVIData }) => {

    return (
        <div className="shadow-md flex flex-col  w-full mx-auto rounded-lg p-4 mt-2">
            <h2 className="text-sm font-semibold mb-2 text-slate-600 uppercase">CURRENT NVDI</h2>
            <div className="border-t border-slate-600 w-full">
                {Object.entries({
                    "Date": `${new Date(data.dt * 1000).toLocaleString()}`,
                    "Max": `${data.data.max} K`,
                    "Mean": `${data.data.mean}`,
                    "Median": `${data.data.median} K`,
                    "Min": `${data.data.min} K`,
                    "Deviation": `${data.data.std} K`,
                    "Num": `${data.data.num} K`,
                }).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-slate-500 py-2 text-sm">
                        <span className="font-medium text-gray-500">{key}</span>
                        <span className="text-gray-500">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AgroMCard = ({ polygonId, type }: AgroMCardProps) => {
    const [data, setData] = useState<WeatherData | null>(null);
    const [soilData, setSoilData] = useState<SoilData | null>(null)
    const [ndviData, setNdviData] = useState<NDVIData | null>(null)

    useEffect(() => {
        fetchCurrentWeather()
        fetchCurrentSoil()
        fetchCurrentNdvi()
    }, [])

    const fetchCurrentWeather = async () => {
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/weather?lat=2.333&lon=33.100&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const weatherData: WeatherData = await polyResponse.json();
            setData(weatherData)
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    const fetchCurrentSoil = async () => {
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/soil?polyid=${polygonId}&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const localData: SoilData = await polyResponse.json();
            setSoilData(localData)
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    const fetchCurrentNdvi = async () => {
        const startDate = Math.floor((Date.now() - 7 * 24 * 60 * 60 * 1000) / 1000);
        const endDate = Math.floor(Date.now() / 1000);
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/ndvi/history?start=${startDate}&end=${endDate}&polyid=${polygonId}&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const localData: NDVIData[] = await polyResponse.json();
            if (localData.length > 0) {
                setNdviData(localData[0])
            }
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    return (
        <div
            className="flex z-10 flex-col w-full rounded-xl items-center"
            style={{
                scrollbarWidth: "thin",
            }}>
            <div
                className="flex flex-col flex-1 items-center justify-center w-full rounded-xl drop-shadow-sm overflow-y-scroll">
                {data && type === 'weather' && <WeatherDisplay data={data} />}

                {ndviData && type === 'nvdi' && <NDVIDisplay data={ndviData} />}

                {soilData && type === 'soil' && <SoilDisplay data={soilData} />}

            </div>
        </div>
    );
};

export default AgroMCard;
